// Professional Design System for Retro Llama Token
// Modern UI/UX with trust-building elements and enhanced visual appeal

export const colors = {
  // Primary Brand Colors (80's Hot Pink)
  primary: {
    main: '#FF0080',
    light: '#FF4DA6',
    dark: '#CC0066',
    contrast: '#FFFFFF',
  },
  
  // Secondary Brand Colors (80's Electric Yellow)
  secondary: {
    main: '#FFFF00',
    light: '#FFFF66',
    dark: '#CCCC00',
    contrast: '#000000',
  },
  
  // 80's Neon Colors
  neon: {
    cyan: '#00FFFF',
    magenta: '#FF00FF',
    green: '#00FF00',
    orange: '#FF8000',
    purple: '#8000FF',
    pink: '#FF0080',
    blue: '#0080FF',
    lime: '#80FF00',
  },
  
  // Success Colors (Trust Indicators)
  success: {
    main: '#4CAF50',
    light: '#81C784',
    dark: '#388E3C',
    contrast: '#FFFFFF',
  },
  
  // Warning Colors
  warning: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
    contrast: '#FFFFFF',
  },
  
  // Error Colors
  error: {
    main: '#F44336',
    light: '#E57373',
    dark: '#D32F2F',
    contrast: '#FFFFFF',
  },
  
  // Neutral Colors
  neutral: {
    white: '#FFFFFF',
    lightGray: '#F5F5F5',
    gray: '#9E9E9E',
    darkGray: '#424242',
    black: '#212121',
  },
  
  // Background Colors
  background: {
    primary: '#0A0A0A',
    secondary: '#1A1A1A',
    tertiary: '#2A2A2A',
    card: '#333333',
    overlay: 'rgba(0, 0, 0, 0.8)',
  },
  
  // Text Colors
  text: {
    primary: '#FFFFFF',
    secondary: '#B0B0B0',
    disabled: '#666666',
    inverse: '#000000',
  },
  
  // Border Colors (80's Style)
  border: {
    primary: '#FF0080',
    secondary: '#FFFF00',
    neon: '#00FFFF',
    neutral: '#444444',
    success: '#00FF00',
    warning: '#FF8000',
    error: '#FF0000',
  },
  
  // Gradient Colors (80's Style)
  gradients: {
    primary: 'linear-gradient(135deg, #FF0080 0%, #FF4DA6 100%)',
    secondary: 'linear-gradient(135deg, #FFFF00 0%, #FFFF66 100%)',
    neon: 'linear-gradient(135deg, #00FFFF 0%, #FF00FF 100%)',
    retro: 'linear-gradient(135deg, #FF8000 0%, #FF0080 100%)',
    success: 'linear-gradient(135deg, #00FF00 0%, #80FF00 100%)',
    background: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)',
    card: 'linear-gradient(135deg, #2A2A2A 0%, #333333 100%)',
    hero: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 50%, #2A2A2A 100%)',
    glass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
    retroGlass: 'linear-gradient(135deg, rgba(255, 0, 128, 0.1) 0%, rgba(255, 255, 0, 0.1) 100%)',
  },
};

export const typography = {
  fontFamily: {
    primary: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    secondary: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    mono: '"Roboto Mono", "Courier New", monospace',
  },
  
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
    black: 900,
  },
  
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem',  // 36px
    '5xl': '3rem',     // 48px
    '6xl': '3.75rem',  // 60px
    '7xl': '4.5rem',   // 72px
    '8xl': '6rem',     // 96px
    '9xl': '8rem',     // 128px
  },
  
  lineHeight: {
    tight: 1.1,
    normal: 1.4,
    relaxed: 1.6,
    loose: 1.8,
  },
  
  letterSpacing: {
    tight: '-0.025em',
    normal: '0em',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
};

export const spacing = {
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  '2xl': '3rem',   // 48px
  '3xl': '4rem',   // 64px
  '4xl': '6rem',   // 96px
  '5xl': '8rem',   // 128px
  '6xl': '12rem',  // 192px
  '7xl': '16rem',  // 256px
  '8xl': '20rem',  // 320px
};

export const borderRadius = {
  none: '0',
  sm: '0.125rem',  // 2px
  base: '0.25rem', // 4px
  md: '0.375rem',  // 6px
  lg: '0.5rem',    // 8px
  xl: '0.75rem',   // 12px
  '2xl': '1rem',   // 16px
  '3xl': '1.5rem', // 24px
  full: '9999px',
};

export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  glow: '0 0 20px rgba(255, 0, 128, 0.4)',
  glowSecondary: '0 0 20px rgba(255, 255, 0, 0.4)',
  neonGlow: '0 0 30px rgba(0, 255, 255, 0.5)',
  retroGlow: '0 0 25px rgba(255, 128, 0, 0.4)',
  glass: '0 8px 32px rgba(0, 0, 0, 0.3)',
  glassHover: '0 12px 40px rgba(0, 0, 0, 0.4)',
};

export const breakpoints = {
  xs: '0px',
  sm: '600px',
  md: '900px',
  lg: '1200px',
  xl: '1536px',
  xxl: '1920px',
};

export const transitions = {
  fast: '0.15s ease-in-out',
  normal: '0.3s ease-in-out',
  slow: '0.5s ease-in-out',
  bounce: '0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  elastic: '0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  smooth: '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
};

// Responsive Design Utilities
export const responsive = {
  mobile: `@media (max-width: ${breakpoints.sm})`,
  tablet: `@media (min-width: ${breakpoints.sm}) and (max-width: ${breakpoints.md})`,
  desktop: `@media (min-width: ${breakpoints.md})`,
  largeDesktop: `@media (min-width: ${breakpoints.lg})`,
  xlDesktop: `@media (min-width: ${breakpoints.xl})`,
  xxlDesktop: `@media (min-width: ${breakpoints.xxl})`,
};

// Enhanced Animation Keyframes
export const animations = {
  fadeIn: {
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  
  slideIn: {
    from: { transform: 'translateX(-100%)' },
    to: { transform: 'translateX(0)' },
  },
  
  slideInUp: {
    from: { transform: 'translateY(100px)', opacity: 0 },
    to: { transform: 'translateY(0)', opacity: 1 },
  },
  
  slideInDown: {
    from: { transform: 'translateY(-100px)', opacity: 0 },
    to: { transform: 'translateY(0)', opacity: 1 },
  },
  
  scaleIn: {
    from: { transform: 'scale(0.8)', opacity: 0 },
    to: { transform: 'scale(1)', opacity: 1 },
  },
  
  pulse: {
    '0%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.05)' },
    '100%': { transform: 'scale(1)' },
  },
  
  glow: {
    '0%': { boxShadow: shadows.glow },
    '50%': { boxShadow: '0 0 30px rgba(230, 0, 122, 0.5)' },
    '100%': { boxShadow: shadows.glow },
  },
  
  float: {
    '0%': { transform: 'translateY(0px)' },
    '50%': { transform: 'translateY(-10px)' },
    '100%': { transform: 'translateY(0px)' },
  },
  
  shimmer: {
    '0%': { backgroundPosition: '-200% 0' },
    '100%': { backgroundPosition: '200% 0' },
  },
  
  rotate: {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  
  bounce: {
    '0%, 20%, 53%, 80%, 100%': { transform: 'translate3d(0,0,0)' },
    '40%, 43%': { transform: 'translate3d(0,-30px,0)' },
    '70%': { transform: 'translate3d(0,-15px,0)' },
    '90%': { transform: 'translate3d(0,-4px,0)' },
  },
};

// Professional Component Styles with Enhanced Visual Appeal
export const componentStyles = {
  // Card Styles
  card: {
    background: colors.background.card,
    borderRadius: borderRadius.xl,
    padding: spacing.xl,
    boxShadow: shadows.glass,
    border: `1px solid ${colors.border.neutral}`,
    transition: transitions.smooth,
    backdropFilter: 'blur(10px)',
    '&:hover': {
      boxShadow: shadows.glassHover,
      transform: 'translateY(-4px)',
    },
  },
  
  // Glass Card Style
  glassCard: {
    background: colors.gradients.glass,
    borderRadius: borderRadius.xl,
    padding: spacing.xl,
    boxShadow: shadows.glass,
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    backdropFilter: 'blur(20px)',
    transition: transitions.smooth,
    '&:hover': {
      boxShadow: shadows.glassHover,
      transform: 'translateY(-4px)',
      border: `1px solid rgba(255, 255, 255, 0.2)`,
    },
  },
  
  // Button Styles
  button: {
    primary: {
      background: colors.gradients.primary,
      color: colors.text.primary,
      borderRadius: borderRadius.xl,
      padding: `${spacing.md} ${spacing.xl}`,
      fontWeight: typography.fontWeight.semiBold,
      fontSize: typography.fontSize.base,
      border: 'none',
      cursor: 'pointer',
      transition: transitions.smooth,
      boxShadow: shadows.md,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
        transition: 'left 0.5s',
      },
      '&:hover': {
        boxShadow: shadows.glow,
        transform: 'translateY(-2px)',
        '&::before': {
          left: '100%',
        },
      },
      '&:active': {
        transform: 'translateY(0)',
      },
      '&:disabled': {
        opacity: 0.6,
        cursor: 'not-allowed',
        transform: 'none',
      },
    },
    
    secondary: {
      background: colors.gradients.secondary,
      color: colors.text.inverse,
      borderRadius: borderRadius.xl,
      padding: `${spacing.md} ${spacing.xl}`,
      fontWeight: typography.fontWeight.semiBold,
      fontSize: typography.fontSize.base,
      border: 'none',
      cursor: 'pointer',
      transition: transitions.smooth,
      boxShadow: shadows.md,
      '&:hover': {
        boxShadow: shadows.glowSecondary,
        transform: 'translateY(-2px)',
      },
    },
    
    outline: {
      background: 'transparent',
      color: colors.primary.main,
      borderRadius: borderRadius.xl,
      padding: `${spacing.md} ${spacing.xl}`,
      fontWeight: typography.fontWeight.semiBold,
      fontSize: typography.fontSize.base,
      border: `2px solid ${colors.primary.main}`,
      cursor: 'pointer',
      transition: transitions.smooth,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '0%',
        height: '100%',
        background: colors.primary.main,
        transition: 'width 0.3s ease',
        zIndex: -1,
      },
      '&:hover': {
        color: colors.text.primary,
        '&::before': {
          width: '100%',
        },
        boxShadow: shadows.glow,
      },
    },
    
    // Gradient Button
    gradient: {
      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
      color: colors.text.primary,
      borderRadius: borderRadius.xl,
      padding: `${spacing.md} ${spacing.xl}`,
      fontWeight: typography.fontWeight.bold,
      fontSize: typography.fontSize.base,
      border: 'none',
      cursor: 'pointer',
      transition: transitions.smooth,
      boxShadow: shadows.md,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.secondary.light} 100%)`,
        opacity: 0,
        transition: 'opacity 0.3s ease',
      },
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: shadows.glow,
        '&::before': {
          opacity: 1,
        },
      },
    },
  },
  
  // Input Styles
  input: {
    background: colors.background.tertiary,
    border: `1px solid ${colors.border.neutral}`,
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    color: colors.text.primary,
    fontSize: typography.fontSize.base,
    transition: transitions.smooth,
    '&:focus': {
      outline: 'none',
      borderColor: colors.primary.main,
      boxShadow: `0 0 0 3px ${colors.primary.main}20`,
    },
    '&:hover': {
      borderColor: colors.primary.light,
    },
  },
  
  // Modal Styles
  modal: {
    background: colors.background.secondary,
    borderRadius: borderRadius['3xl'],
    padding: spacing.xl,
    boxShadow: shadows['2xl'],
    border: `1px solid ${colors.border.neutral}`,
    maxWidth: '500px',
    width: '90%',
    margin: 'auto',
    backdropFilter: 'blur(20px)',
  },
  
  // Alert Styles
  alert: {
    success: {
      background: colors.success.main,
      color: colors.text.primary,
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      border: `1px solid ${colors.success.dark}`,
    },
    
    warning: {
      background: colors.warning.main,
      color: colors.text.primary,
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      border: `1px solid ${colors.warning.dark}`,
    },
    
    error: {
      background: colors.error.main,
      color: colors.text.primary,
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      border: `1px solid ${colors.error.dark}`,
    },
    
    info: {
      background: colors.primary.main,
      color: colors.text.primary,
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      border: `1px solid ${colors.primary.dark}`,
    },
  },
  
  // Status Indicator Styles
  statusIndicator: {
    success: {
      background: colors.success.main,
      color: colors.text.primary,
      borderRadius: borderRadius.full,
      padding: `${spacing.xs} ${spacing.sm}`,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
    },
    
    warning: {
      background: colors.warning.main,
      color: colors.text.primary,
      borderRadius: borderRadius.full,
      padding: `${spacing.xs} ${spacing.sm}`,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
    },
    
    pending: {
      background: colors.warning.main,
      color: colors.text.primary,
      borderRadius: borderRadius.full,
      padding: `${spacing.xs} ${spacing.sm}`,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
    },
  },
  
  // Hero Section Styles
  hero: {
    background: colors.gradients.hero,
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
    paddingTop: '100px', // Account for sticky header height
    [responsive.desktop]: {
      paddingTop: '110px',
    },
    [responsive.largeDesktop]: {
      paddingTop: '120px',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 80%, rgba(230, 0, 122, 0.15) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 80% 20%, rgba(255, 235, 59, 0.15) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
  },
  
  // Section Styles
  section: {
    padding: `${spacing['4xl']} 0`,
    position: 'relative',
  },
  
  // Container Styles
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: `0 ${spacing.lg}`,
    width: '100%',
    boxSizing: 'border-box',
    [responsive.desktop]: {
      maxWidth: '1400px',
      padding: `0 ${spacing['2xl']}`,
    },
    [responsive.largeDesktop]: {
      maxWidth: '1600px',
      padding: `0 ${spacing['3xl']}`,
    },
    [responsive.xlDesktop]: {
      maxWidth: '1800px',
      padding: `0 ${spacing['4xl']}`,
    },
    [responsive.xxlDesktop]: {
      maxWidth: '2000px',
      padding: `0 ${spacing['5xl']}`,
    },
  },

  // Section Container - for full-width sections with contained content
  sectionContainer: {
    width: '100%',
    padding: `${spacing['4xl']} 0`,
    [responsive.desktop]: {
      padding: `${spacing['5xl']} 0`,
    },
    [responsive.largeDesktop]: {
      padding: `${spacing['6xl']} 0`,
    },
  },

  // Content Container - for consistent content width
  contentContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: `0 ${spacing.lg}`,
    width: '100%',
    boxSizing: 'border-box',
    [responsive.desktop]: {
      maxWidth: '1400px',
      padding: `0 ${spacing['2xl']}`,
    },
    [responsive.largeDesktop]: {
      maxWidth: '1600px',
      padding: `0 ${spacing['3xl']}`,
    },
    [responsive.xlDesktop]: {
      maxWidth: '1800px',
      padding: `0 ${spacing['4xl']}`,
    },
    [responsive.xxlDesktop]: {
      maxWidth: '2000px',
      padding: `0 ${spacing['5xl']}`,
    },
  },
};

const designSystem = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  breakpoints,
  transitions,
  componentStyles,
  animations,
  responsive,
};

export default designSystem; 